import { useMemo } from 'react'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    useMaterialReactTable,
} from 'material-react-table'
import { type DictionaryArray, type DocumentUpload } from '../types'
import { type APIDataSet } from '@visorpro/client'
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material'
import { Link } from 'react-router-dom'
import { Error } from '@mui/icons-material'

interface DocumentUploadTableProps {
    documentUploads: DocumentUpload[]
    dataSets: DictionaryArray<APIDataSet>
}

export const DocumentUploadsTable = ({
    documentUploads,
    dataSets,
}: DocumentUploadTableProps) => {
    const columns = useMemo<MRT_ColumnDef<DocumentUpload>[]>(
        () => [
            {
                id: 'status',
                header: '',
                columnDefType: 'display',
                size: 20,
                accessor: (row: DocumentUpload) => row.status,
                Cell: ({ row }) => {
                    let component = null
                    let tooltip = null

                    if (row.original.status === 'error') {
                        tooltip = 'Error'
                        component = <Error color="error" />
                    } else if (
                        row.original.status === 'pending' ||
                        row.original.status === 'uploading'
                    ) {
                        const progress = row.original.progress
                        tooltip = `Uploading ${progress}%`
                        component = (
                            <Box position="relative" display="inline-flex">
                                <CircularProgress
                                    variant="determinate"
                                    value={progress}
                                    size={20}
                                    thickness={8}
                                    color="warning"
                                    sx={{ zIndex: 2 }}
                                />
                                <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={20}
                                    thickness={8}
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.grey[600],
                                        position: 'absolute',
                                        left: 0,
                                        zIndex: 1,
                                    }}
                                />
                            </Box>
                        )
                    }

                    return (
                        <Tooltip title={tooltip}>
                            <Box
                                display="flex"
                                justifyContent="center"
                                width="100%">
                                {component}
                            </Box>
                        </Tooltip>
                    )
                },
            },
            {
                id: 'title',
                header: 'Title',
                accessorFn: (row) => row.file.name,
            },
            {
                id: 'data_set',
                header: 'Data Set',
                accessorFn: (row) => row.data_set_id,
                Cell: ({ cell }) => {
                    const dataSetId = cell.getValue<string>()
                    const dataSet = dataSets.get(dataSetId)
                    return (
                        <Link to={`/data-sets/${dataSetId}`}>
                            <Chip
                                label={dataSet ? dataSet.name : dataSetId}
                                size="small"
                            />
                        </Link>
                    )
                },
            },
        ],
        [dataSets],
    )

    const table = useMaterialReactTable({
        columns,
        data: documentUploads,
        enableKeyboardShortcuts: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enablePagination: false,
        enableSorting: false,
        enableTopToolbar: false,
        enableTableHead: false,
        enableTableFooter: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.background.paper,
        }),
        state: {
            density: 'compact',
        },
        muiTablePaperProps: { sx: { height: '100%', width: '100%' } },
        muiTableContainerProps: { sx: { height: '100%', width: '100%' } },
    })

    return <MaterialReactTable table={table} />
}
